import React, {useEffect, useState, useRef} from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Typography, Box, Button, Stack, Chip, TablePagination,
} from '@mui/material';
import {InfoOutlined} from '@mui/icons-material';
import UserListAside from '@/pages/components/manage/sys/user/UserListAside';
import UserDetail from '@/pages/components/manage/sys/user/UserDetail';
import {UserService} from '@/services';
import {UserPageData} from "@/services/manageService/userService";
import {useTheme} from "@mui/material/styles";
import {SnackbarField, PopoverButton} from '@/components/common';
import {usePageCrud} from '@/hooks/usePageCrud';

const UserList: React.FC = () => {
    const theme = useTheme();
    const [data, setData] = useState<UserPageData>({records: [], total: 0, pageNum: 1, pageSize: 25});
    const [loading, setLoading] = useState(false);
    const [userDetailOpen, setUserDetailOpen] = useState(false);
    const [isAdd, setIsAdd] = useState(true);
    const [detailUserId, setDetailUserId] = useState<number | undefined>(undefined);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error';
    }>({open: false, message: '', severity: 'success'});
    const [selectedDeptId, setSelectedDeptId] = useState<number | undefined>();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const lastActiveButtonRef = useRef<HTMLButtonElement | null>(null);
    const {canRead, canCreate, canRemove} = usePageCrud({
        read: ['admin_user_query', 'admin_user_detail'],
        create: 'admin:user:create',
        update: 'admin:user:update',
        remove: 'admin:user:delete',
    });

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDeptId, page, pageSize]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const payload = { pageNumber: page + 1, pageSize, pageType: 1, deptId: selectedDeptId };
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.debug('[UserList] queryUserPage payload:', payload);
            }
            const res = await UserService.queryUserPage(payload);
            setData({
                records: res.list,
                total: res.totalRow,
                pageNum: res.pageNumber,
                pageSize: res.pageSize,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = (e?: React.MouseEvent<HTMLButtonElement>) => {
        setIsAdd(true);
        setUserDetailOpen(true);
        if (e && e.currentTarget) lastActiveButtonRef.current = e.currentTarget;
    };

    const handleUserDetailClose = (data: any) => {
        if (data && data.message) {
            setSnackbar({
                open: true,
                message: data.message || (data.type === 'create' ? data.username +  ' Generated successful! User Password: ' + (data.password || '') : data.username + ' Updated successful!'),
                severity: data.severity || 'success'
            });
        }
        setUserDetailOpen(false);
        fetchData();
        setTimeout(() => {
            lastActiveButtonRef.current?.focus();
        }, 0);
    };

    const handleDetail = (userId: number, e?: React.MouseEvent<HTMLButtonElement>) => {
        setIsAdd(false);
        setDetailUserId(userId);
        setUserDetailOpen(true);
        if (e && e.currentTarget) lastActiveButtonRef.current = e.currentTarget;
    };

    const handleRemove = async (userId: number) => {
        try {
            let res = await UserService.deleteUser(userId);
            res && res.code === '200' && setSnackbar({
                open: true,
                message: 'User Remove successful!',
                severity: 'success'
            });
            fetchData();
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'User Remove failed!';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const handleDeptSelect = (deptId: string | number) => {
        setSelectedDeptId(typeof deptId === 'string' ? parseInt(deptId, 10) : deptId);
        setPage(0);
    };

    const handleCloseSnackbar = () => setSnackbar({...snackbar, open: false});

    return (
        <Box
            display="flex"
            sx={{
                p: 2.5,
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            <Box minWidth={220} mr={2} flexShrink={0} sx={{overflow: 'auto'}}>
                <UserListAside onDeptSelect={handleDeptSelect} />
            </Box>
            <Box flex={1} minWidth={0} display="flex" flexDirection="column" overflow="hidden">
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">
                        User List
                    </Typography>
                    {canCreate && (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleAdd}
                        ref={lastActiveButtonRef}
                        sx={{
                            borderRadius: theme.shape.borderRadius,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: theme.shadows[1],
                            '&:hover': {boxShadow: theme.shadows[3]}
                        }}
                    >
                        ADD
                    </Button>
                    )}
                </Stack>
                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[1],
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}}>Username</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Name</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Email</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Dept</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Supervisor</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Status</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.records.map(user => (
                                <TableRow key={user.userId} hover sx={{
                                    cursor: 'pointer',
                                    transition: 'background 0.2s',
                                    '&:hover': {background: theme.palette.action.selected}
                                }}>
                                    <TableCell>{user.username}</TableCell>
                                    <TableCell>{user.firstName} {user.lastName}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>{user.deptName}</TableCell>
                                    <TableCell>{(user.supervisorFirstName || user.supervisorLastName) ? `${user.supervisorFirstName || ''} ${user.supervisorLastName || ''}`.trim() : '-'}</TableCell>
                                    <TableCell>
                                        {user.status === 1 && <Chip label="Normal" color="success" size="small"/>}
                                        {user.status === 0 && <Chip label="Disabled" color="error" size="small"/>}
                                        {user.status === 2 && <Chip label="Unactivated" color="warning" size="small"/>}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {canRead && (
                                            <Button size="small" variant="outlined"
                                                    sx={{
                                                        minWidth: 80,
                                                        textTransform: 'none',
                                                        borderColor: theme.palette.secondary.main,
                                                        color: theme.palette.secondary.main,
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            borderColor: theme.palette.secondary.dark,
                                                            color: theme.palette.secondary.dark
                                                        }
                                                    }}
                                                    startIcon={<InfoOutlined/>}
                                                    ref={lastActiveButtonRef}
                                                    onClick={e => handleDetail(user.userId, e)}>
                                                Detail
                                            </Button>
                                            )}
                                            {canRemove && String(user.username ?? '').toLowerCase() !== 'superadmin' && (
                                            <PopoverButton
                                                nodeId={user.userId}
                                                nodeName={user.username}
                                                onRemove={handleRemove}
                                                theme={theme}
                                            />
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {data.records.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {loading ? 'Loading...' : 'NO DATA YET'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data.total || 0}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10) || 25);
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{flexShrink: 0}}
                />
                <UserDetail
                    open={userDetailOpen}
                    onClose={handleUserDetailClose}
                    isAdd={isAdd}
                    userId={detailUserId}
                />
            </Box>
            <SnackbarField
                open={snackbar.open}
                message={snackbar.message ?? ""}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </Box>
    );
};

export default UserList;
